// Intent: the AUTHENTICATED half of the mobile API (P2 linking + the linked
// Polfinance portfolio). One authFetch attaches the in-memory access token and
// transparently refreshes it ONCE on 401 (access tokens live 15 min by design),
// so screens never deal with token lifecycles. Mock transport mirrors the real
// endpoints while EXPO_PUBLIC_API_URL is unset — same rule as auth-api.

import { Platform } from 'react-native';

import { API_URL, ApiEnvelope } from '@/api/client';
import { AuthPayload, AuthResult, refreshSession } from '@/api/auth-api';
import { getDeviceId } from '@/api/device-id';
import { getRefreshToken } from '@/api/token-storage';
import { useAuthStore } from '@/store/auth-store';

/** Mirror of MobilePolicyDto (backend) — a read-only Polfinance policy. */
export interface PolfinancePolicy {
  id: number;
  policyNumber: string | null;
  insurerName: string | null;
  productName: string | null;
  premiumGr: number | null;
  premiumPeriod: string | null;
  startDate: string | null;
  endDate: string | null;
  active: boolean | null;
  nextTechnicalExamDate: string | null;
  /** Presigned (1h) URL to the policy's document/scan in the CRM, if any. */
  documentUrl?: string | null;
  documentName?: string | null;
  documentContentType?: string | null;
}

export interface LinkHelpInput {
  firstName: string;
  lastName: string;
  otherPhone: string | null;
  hint: string | null;
}

/** Mirror of the /me payload — account + match, no tokens. */
export interface MeSnapshot {
  account: { id: number; phone: string; firstName: string | null; linkStatus: string };
  matchStatus: 'NONE' | 'SINGLE_MASKED' | 'MULTIPLE' | 'LINKED';
  maskedMatch: { name: string; policiesCount: number } | null;
  /** Presentation-only agent-mode flag — see AuthPayload.isAgent. */
  isAgent?: boolean;
  /** Presentation-only admin-mode flag — see AuthPayload.isAdmin. */
  isAdmin?: boolean;
}

/**
 * Server truth about the account. Needed because linkStatus can change WITHOUT
 * the app doing anything (office links the account in the CRM, help request gets
 * resolved) — the local snapshot would stay stale forever otherwise. Mock mode
 * has no server truth, so it reports not-ok and callers keep local state.
 */
export async function fetchMeApi(): Promise<AuthResult<MeSnapshot>> {
  if (!API_URL) return { ok: false, message: null };
  const res = await authFetch<MeSnapshot>('/api/mobile/me', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function confirmLinkApi(): Promise<AuthResult<string>> {
  if (!API_URL) return mockLink();
  const res = await authFetch<string>('/api/mobile/auth/link/confirm', 'POST', {});
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function linkByPeselApi(pesel: string): Promise<AuthResult<string>> {
  if (!API_URL) return mockLinkByPesel(pesel);
  const res = await authFetch<string>('/api/mobile/auth/link/pesel', 'POST', { pesel });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function linkHelpApi(input: LinkHelpInput): Promise<AuthResult<string>> {
  if (!API_URL) return { ok: true, message: 'Zgłoszenie przyjęte.', data: 'HELP_REQUESTED' };
  const res = await authFetch<string>('/api/mobile/auth/link/help', 'POST', input);
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/** Registers this install's Expo push token server-side (app_device row). */
export async function registerDeviceApi(
  expoPushToken: string,
  platform: string,
): Promise<AuthResult<undefined>> {
  if (!API_URL) return { ok: true, message: null };
  const { getDeviceId } = await import('@/api/device-id');
  const deviceId = await getDeviceId();
  const res = await authFetch<undefined>('/api/mobile/devices', 'POST', {
    deviceId,
    expoPushToken,
    platform,
    pushEnabled: true,
  });
  return { ok: res.status, message: res.message };
}

/** Server sync state — payload is the opaque snapshot JSON (null if never synced). */
export interface SyncState {
  payload: string | null;
  clientUpdatedAt: number | null;
  accepted: boolean;
}

export async function pullSyncApi(): Promise<AuthResult<SyncState>> {
  if (!API_URL) return { ok: false, message: null };
  const res = await authFetch<SyncState>('/api/mobile/sync', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function pushSyncApi(payload: string, clientUpdatedAt: number): Promise<AuthResult<SyncState>> {
  if (!API_URL) return { ok: false, message: null };
  const res = await authFetch<SyncState>('/api/mobile/sync', 'PUT', { payload, clientUpdatedAt });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// --- Household (realny sync gospodarstwa) — mirror of MobileHouseholdController.
// Every call degrades gracefully: on the current prod backend (1.52) these
// endpoints 404, so callers treat not-ok as "feature unavailable". ---

export interface HouseholdMemberDto {
  maskedPhone: string;
  role: string; // OWNER / PARTNER
  joinedAt: string;
}
export interface HouseholdInfoDto {
  id: number;
  name: string;
  inviteCode: string;
  members: HouseholdMemberDto[];
}

/** Create a server household (409 if the account already belongs to one). */
export async function createHouseholdApi(name: string): Promise<AuthResult<HouseholdInfoDto>> {
  if (!API_URL) {
    return { ok: true, message: null, data: { id: 1, name, inviteCode: 'HH-DEM-000', members: [] } };
  }
  const res = await authFetch<HouseholdInfoDto>('/api/mobile/household', 'POST', { name });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/** Join by invite code (404 unknown, 409 already a member). */
export async function joinHouseholdApi(code: string): Promise<AuthResult<HouseholdInfoDto>> {
  if (!API_URL) {
    return { ok: true, message: null, data: { id: 1, name: 'Wspólne gospodarstwo', inviteCode: code, members: [] } };
  }
  const res = await authFetch<HouseholdInfoDto>('/api/mobile/household/join', 'POST', { code });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/** Current household info, or not-ok/404 when the account is in none. */
export async function fetchHouseholdApi(): Promise<AuthResult<HouseholdInfoDto>> {
  if (!API_URL) return { ok: false, message: null };
  const res = await authFetch<HouseholdInfoDto>('/api/mobile/household', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function leaveHouseholdApi(): Promise<AuthResult<undefined>> {
  if (!API_URL) return { ok: true, message: null };
  const res = await authFetch<undefined>('/api/mobile/household/leave', 'POST', {});
  return { ok: res.status, message: res.message };
}

/** Shared-household sync — identical LWW contract to the account sync. */
export async function pullHouseholdSyncApi(): Promise<AuthResult<SyncState>> {
  if (!API_URL) return { ok: false, message: null };
  const res = await authFetch<SyncState>('/api/mobile/household/sync', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function pushHouseholdSyncApi(payload: string, clientUpdatedAt: number): Promise<AuthResult<SyncState>> {
  if (!API_URL) return { ok: false, message: null };
  const res = await authFetch<SyncState>('/api/mobile/household/sync', 'PUT', { payload, clientUpdatedAt });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// --- Policy-file cloud backup (opt-in "Kopia skanów polis") ---------------

export interface PolicyFileDto {
  id: number;
  label: string | null;
  policyClientId: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  healthConsent: boolean;
  createdAt: string;
  downloadUrl: string | null;
}

/** Upload a policy scan to the cloud backup (multipart, no Content-Type set). */
export async function uploadPolicyFileApi(
  file: { uri: string; name: string; mimeType: string },
  opts: { label?: string; policyClientId?: string; healthConsent?: boolean },
): Promise<AuthResult<PolicyFileDto>> {
  if (!API_URL) return { ok: true, message: null };
  const fields: Record<string, string> = { healthConsent: opts.healthConsent ? 'true' : 'false' };
  if (opts.label) fields.label = opts.label;
  if (opts.policyClientId) fields.policyClientId = opts.policyClientId;
  const res = await authUploadFile<PolicyFileDto>('/api/mobile/policy-files', file, fields);
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function listPolicyFilesApi(): Promise<AuthResult<PolicyFileDto[]>> {
  if (!API_URL) return { ok: true, message: null, data: [] };
  const res = await authFetch<PolicyFileDto[]>('/api/mobile/policy-files', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function deletePolicyFileApi(id: number): Promise<AuthResult<undefined>> {
  if (!API_URL) return { ok: true, message: null };
  const res = await authFetch<undefined>(`/api/mobile/policy-files/${id}`, 'DELETE');
  return { ok: res.status, message: res.message };
}

/** Mirror of MobileReferralInfoDto — my code + share link (50 zł program). */
export interface ReferralInfo {
  code: string;
  shareUrl: string;
  rewardPln: number;
}

export async function fetchReferralInfoApi(): Promise<AuthResult<ReferralInfo>> {
  if (!API_URL) return mockReferralInfo();
  const res = await authFetch<ReferralInfo>('/api/mobile/referral', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/**
 * Mirror of MobileReferralCaseDto — one referral tracked in "Moje polecenia".
 * The voucher e-code arrives ONLY after pickup was confirmed (COLLECTED).
 */
export interface ReferralCase {
  id: number;
  display: string | null;
  status: 'SUBMITTED' | 'PURCHASED' | 'VOUCHER_GRANTED' | 'VOUCHER_COLLECTED' | 'REJECTED';
  createdAt: string | null;
  purchasedAt: string | null;
  grantedAt: string | null;
  collectedAt: string | null;
  amountPln: number | null;
  voucherCode: string | null;
}

export async function fetchReferralCasesApi(): Promise<AuthResult<ReferralCase[]>> {
  if (!API_URL) return { ok: true, message: null, data: [] };
  const res = await authFetch<ReferralCase[]>('/api/mobile/referral/cases', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/**
 * Digital voucher pickup: recipient data (tax evidence, replaces the paper
 * signature at the office) unlocks the gift-card e-code in the response.
 */
export async function collectReferralVoucherApi(
  id: number,
  input: { firstName: string; lastName: string; address?: string },
): Promise<AuthResult<ReferralCase>> {
  if (!API_URL) return { ok: true, message: null };
  const res = await authFetch<ReferralCase>(`/api/mobile/referral/cases/${id}/collect`, 'POST', input);
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/** Mirror of MobileAgentDto — the "Mój agent" business card (name + phone). */
export interface AgentCard {
  name: string | null;
  phone: string | null;
}

export async function fetchAgentApi(): Promise<AuthResult<AgentCard>> {
  if (!API_URL) return mockAgent();
  const res = await authFetch<AgentCard>('/api/mobile/agent', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function fetchPolfinancePolicies(): Promise<AuthResult<PolfinancePolicy[]>> {
  if (!API_URL) return mockPolicies();
  const res = await authFetch<PolfinancePolicy[]>('/api/mobile/policies', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// ------------------------------------------------------------- agent mode
// The AGENT tab (agents only). Server re-verifies agent-ness on every call —
// these endpoints 403 for regular clients no matter what the local flag says.

/** Mirror of MobileAgentClientDto — one row of the agent's "Moi klienci" list. */
export interface AgentClient {
  id: number;
  name: string;
  phone: string | null;
  city: string | null;
  policiesCount: number;
  lastPolicyEndDate: string | null;
  hasApp: boolean;
}

/** Mirror of MobileAgentClientDetailDto — the read-only client card. */
export interface AgentClientDetail {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  city: string | null;
  hasApp: boolean;
  policies: PolfinancePolicy[];
}

export async function fetchAgentClientsApi(): Promise<AuthResult<AgentClient[]>> {
  if (!API_URL) return mockAgentClients();
  const res = await authFetch<AgentClient[]>('/api/mobile/agent-mode/clients', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function fetchAgentClientApi(customerId: number): Promise<AuthResult<AgentClientDetail>> {
  if (!API_URL) return mockAgentClientDetail(customerId);
  const res = await authFetch<AgentClientDetail>(`/api/mobile/agent-mode/clients/${customerId}`, 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// ---------------------------------------------------------------- chat (APP)
// Client side: one OPEN conversation with the office, pinned to the linked CRM
// customer. Agent side (agent mode): conversations of the agent's own clients.

export interface ChatMessage {
  id: number;
  author: 'VISITOR' | 'AGENT' | 'SYSTEM';
  body: string;
  createdAt: string | null;
}

export interface ChatPollResult {
  conversationId?: number;
  status: 'OPEN' | 'CLOSED' | 'NONE';
  agentJoined: boolean;
  messages: ChatMessage[];
}

/** Mirror of ChatConversationDto (subset the phone list renders). */
export interface AgentChatConversation {
  id: number;
  status: string;
  channel: string;
  visitorName: string | null;
  lastMessagePreview: string | null;
  lastVisitorMsgAt: string | null;
  unread: boolean;
  /** WWW conversations: 'polfinance' | 'kredytowyprzewodnik'; null on APP. */
  site?: string | null;
  /** CRM user who replied first (admin view: "someone is already on it"). */
  assignedUserName?: string | null;
}

export async function startChatApi(): Promise<AuthResult<ChatPollResult>> {
  if (!API_URL) return mockChatStart();
  const res = await authFetch<ChatPollResult>('/api/mobile/chat/start', 'POST', {});
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function sendChatMessageApi(body: string): Promise<AuthResult<ChatMessage>> {
  if (!API_URL) return mockChatSend(body);
  const res = await authFetch<ChatMessage>('/api/mobile/chat/message', 'POST', { body });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function pollChatApi(afterId: number): Promise<AuthResult<ChatPollResult>> {
  if (!API_URL) return mockChatPoll(afterId);
  const res = await authFetch<ChatPollResult>(`/api/mobile/chat/poll?afterId=${afterId}`, 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function fetchAgentConversationsApi(): Promise<AuthResult<AgentChatConversation[]>> {
  if (!API_URL) return mockAgentConversations();
  const res = await authFetch<AgentChatConversation[]>('/api/mobile/agent-mode/chat/conversations', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export interface AgentChatMessagesResult {
  conversation: AgentChatConversation;
  messages: ChatMessage[];
}

export async function fetchAgentMessagesApi(
  conversationId: number,
  afterId: number,
): Promise<AuthResult<AgentChatMessagesResult>> {
  if (!API_URL) return mockAgentMessages(conversationId, afterId);
  const res = await authFetch<AgentChatMessagesResult>(
    `/api/mobile/agent-mode/chat/conversations/${conversationId}/messages?afterId=${afterId}`,
    'GET',
  );
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function sendAgentReplyApi(
  conversationId: number,
  body: string,
): Promise<AuthResult<ChatMessage>> {
  if (!API_URL) return mockChatSend(body);
  const res = await authFetch<ChatMessage>(
    `/api/mobile/agent-mode/chat/conversations/${conversationId}/message`,
    'POST',
    { body },
  );
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// ---- admin mode (auth-store.isAdmin) -------------------------------------
// Same shapes as agent mode, but the server returns ALL conversations (WWW of
// both websites + APP). Authorization is server-side: the account's phone must
// match a CRM admin, re-checked on every call.

export async function fetchAdminConversationsApi(): Promise<AuthResult<AgentChatConversation[]>> {
  if (!API_URL) return mockAgentConversations();
  const res = await authFetch<AgentChatConversation[]>(
    '/api/mobile/agent-mode/admin/chat/conversations',
    'GET',
  );
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function fetchAdminMessagesApi(
  conversationId: number,
  afterId: number,
): Promise<AuthResult<AgentChatMessagesResult>> {
  if (!API_URL) return mockAgentMessages(conversationId, afterId);
  const res = await authFetch<AgentChatMessagesResult>(
    `/api/mobile/agent-mode/admin/chat/conversations/${conversationId}/messages?afterId=${afterId}`,
    'GET',
  );
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function sendAdminReplyApi(
  conversationId: number,
  body: string,
): Promise<AuthResult<ChatMessage>> {
  if (!API_URL) return mockChatSend(body);
  const res = await authFetch<ChatMessage>(
    `/api/mobile/agent-mode/admin/chat/conversations/${conversationId}/message`,
    'POST',
    { body },
  );
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/** Mirror of MobilePolicyScanResultDto — AI draft for the add-policy form. */
export interface PolicyScanDraft {
  policyType: string;
  insurerName: string | null;
  policyNumber: string | null;
  subject: string | null;
  premiumGr: number | null;
  premiumPeriod: string | null;
  startDate: string | null;
  endDate: string | null;
  warning: string | null;
}

export interface ScanFileInput {
  uri: string;
  name: string;
  mimeType: string;
}

/**
 * Uploads a policy photo for AI reading. The server processes it in memory and
 * never stores the file — the result only prefills the local add-policy form.
 */
export async function scanPolicyApi(file: ScanFileInput): Promise<AuthResult<PolicyScanDraft>> {
  if (!API_URL) return mockScanDraft();
  const res = await authUploadFile<PolicyScanDraft>('/api/mobile/policy-scan', file);
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/**
 * GUEST AI scan (1.2.0) — no login needed, capped server-side per device
 * (4/day + 8 lifetime, then the login wall / HTTP 429). Same result contract
 * as scanPolicyApi; file is processed in memory and never stored.
 */
export async function guestScanPolicyApi(file: ScanFileInput): Promise<AuthResult<PolicyScanDraft>> {
  if (!API_URL) return mockGuestScanDraft();
  const deviceId = await getDeviceId();
  const res = await publicUploadFile<PolicyScanDraft>('/api/public/mobile/policy-scan', file, { deviceId });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

// --------------------------------------------- phone attach / restore 2FA
// 1.2.0: e-mail/Google sessions attach or confirm the phone by SMS. Verify
// returns FRESH tokens (SMS-verified) — the caller must applySession them,
// otherwise the cloud-restore pull stays locked.

export async function requestAccountPhoneApi(phone?: string): Promise<AuthResult<undefined>> {
  if (!API_URL) return mockRequestAccountPhone();
  const deviceId = await getDeviceId();
  const res = await authFetch<undefined>('/api/mobile/account/phone/request', 'POST', { phone, deviceId });
  return { ok: res.status, message: res.message };
}

export async function verifyAccountPhoneApi(
  code: string,
  phone?: string,
): Promise<AuthResult<AuthPayload>> {
  if (!API_URL) return mockVerifyAccountPhone(code, phone);
  const deviceId = await getDeviceId();
  const res = await authFetch<AuthPayload>('/api/mobile/account/phone/verify', 'POST', {
    code,
    phone,
    deviceId,
  });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/** Consent switches (marketing/analytics); omitted field = unchanged. */
export async function updateConsentsApi(consents: {
  marketingConsent?: boolean;
  analyticsConsent?: boolean;
}): Promise<AuthResult<undefined>> {
  if (!API_URL) return { ok: true, message: 'Zapisano zgody.' };
  const res = await authFetch<undefined>('/api/mobile/account/consents', 'POST', consents);
  return { ok: res.status, message: res.message };
}

// ------------------------------------------------------------- protection quote
// "Zapytaj agenta o wycenę": creates a CRM lead routed to the client's caretaker
// agent (stage D of agent mode). Status endpoint drives the button state.

export interface QuoteStatus {
  pending: boolean;
  salesLeadId?: number;
}

export async function requestProtectionQuoteApi(summary: string): Promise<AuthResult<QuoteStatus>> {
  if (!API_URL) {
    mockQuotePending = true;
    return { ok: true, message: 'Zgłoszenie wysłane do Twojego opiekuna — skontaktuje się z Tobą.', data: { pending: true, salesLeadId: 500 } };
  }
  const res = await authFetch<QuoteStatus>('/api/mobile/leads/protection-quote', 'POST', { summary });
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

export async function fetchQuoteStatusApi(): Promise<AuthResult<QuoteStatus>> {
  if (!API_URL) return { ok: true, message: null, data: { pending: mockQuotePending } };
  const res = await authFetch<QuoteStatus>('/api/mobile/leads/protection-quote/status', 'GET');
  return { ok: res.status, message: res.message, data: res.data ?? undefined };
}

/**
 * Attach the client's scanned policy to their just-created quote lead. Called
 * right after requestProtectionQuoteApi returns a salesLeadId, only when the
 * client consented (renewal modal). Best-effort — a failed upload never blocks
 * the quote itself. Ownership is enforced server-side.
 */
export async function attachQuoteDocumentApi(
  salesLeadId: number,
  file: { uri: string; name: string; mimeType: string },
): Promise<AuthResult<null>> {
  if (!API_URL) return { ok: true, message: null, data: null };
  const res = await authUploadFile<null>(
    `/api/mobile/leads/protection-quote/${salesLeadId}/document`,
    file,
  );
  return { ok: res.status, message: res.message ?? null, data: null };
}

let mockQuotePending = false;

// ------------------------------------------------------------------ transport

async function authFetch<T>(path: string, method: string, body?: unknown): Promise<ApiEnvelope<T>> {
  let response: Response;
  try {
    let token = useAuthStore.getState().accessToken ?? (await tryRefresh());
    response = await rawFetch(path, method, body, token);
    if (response.status === 401) {
      // Access token expired mid-session: one silent refresh, one retry.
      token = await tryRefresh();
      if (token) response = await rawFetch(path, method, body, token);
    }
  } catch {
    // Offline / DNS / TLS / refresh failure: a calm envelope, never a thrown
    // rejection that a screen would have to guard against.
    return { status: false, message: 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.', data: null };
  }
  try {
    return (await response.json()) as ApiEnvelope<T>;
  } catch {
    return { status: false, message: 'Brak połączenia z serwerem. Spróbuj ponownie.', data: null };
  }
}

// Multipart twin of authFetch: same token + single-silent-refresh dance, but
// WITHOUT a Content-Type header — fetch must set the multipart boundary itself.
async function authUpload<T>(path: string, form: FormData): Promise<ApiEnvelope<T>> {
  let response: Response;
  try {
    let token = useAuthStore.getState().accessToken ?? (await tryRefresh());
    response = await rawUpload(path, form, token);
    if (response.status === 401) {
      // Access token expired mid-session: one silent refresh, one retry.
      token = await tryRefresh();
      if (token) response = await rawUpload(path, form, token);
    }
  } catch {
    // Network-layer rejection (no internet, DNS/TLS, refresh call failed). This
    // MUST be caught here: the raw fetch used to bubble up and callers (e.g. the
    // policy scan) mislabeled it as a 75 s "read timed out". Honest message instead.
    return { status: false, message: 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.', data: null };
  }
  // Still 401 after the refresh attempt = guest or a session that can't refresh.
  // The mobile chain answers 401 with a non-envelope body, so say it plainly.
  if (response.status === 401) {
    return { status: false, message: 'Zaloguj się w zakładce Więcej → Konto, aby użyć tej funkcji.', data: null };
  }
  try {
    return (await response.json()) as ApiEnvelope<T>;
  } catch {
    return { status: false, message: 'Serwer zwrócił nieoczekiwaną odpowiedź. Spróbuj ponownie.', data: null };
  }
}

/**
 * File upload that is ROBUST on native. On Android/iOS a multipart `fetch()`
 * with a `file://` part frequently rejects immediately with "Network request
 * failed" — this was the real cause of the policy-scan failure (JSON calls and
 * cloud sync worked, only the file upload died, and the screen mislabeled it as
 * a timeout). Native path streams the file through expo-file-system's
 * `uploadAsync` (native OkHttp), which handles file:// and large files. Web
 * keeps the FormData+fetch path via authUpload. Same token + one-refresh dance.
 */
async function authUploadFile<T>(
  path: string,
  file: { uri: string; name: string; mimeType: string },
  fields: Record<string, string> = {},
): Promise<ApiEnvelope<T>> {
  if (Platform.OS === 'web') {
    const form = new FormData();
    const blob = await (await fetch(file.uri)).blob();
    form.append('file', new File([blob], file.name, { type: file.mimeType }));
    for (const [k, v] of Object.entries(fields)) form.append(k, v);
    return authUpload<T>(path, form);
  }

  const { uploadAsync, FileSystemUploadType } = await import('expo-file-system/legacy');
  const send = (token: string | null) =>
    uploadAsync(`${API_URL}${path}`, file.uri, {
      httpMethod: 'POST',
      uploadType: FileSystemUploadType.MULTIPART,
      fieldName: 'file',
      mimeType: file.mimeType,
      parameters: fields,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

  let result: Awaited<ReturnType<typeof send>>;
  try {
    let token = useAuthStore.getState().accessToken ?? (await tryRefresh());
    result = await send(token);
    if (result.status === 401) {
      token = await tryRefresh();
      if (token) result = await send(token);
    }
  } catch {
    return { status: false, message: 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.', data: null };
  }
  if (result.status === 401) {
    return { status: false, message: 'Zaloguj się w zakładce Więcej → Konto, aby użyć tej funkcji.', data: null };
  }
  try {
    return JSON.parse(result.body) as ApiEnvelope<T>;
  } catch {
    return { status: false, message: 'Serwer zwrócił nieoczekiwaną odpowiedź. Spróbuj ponownie.', data: null };
  }
}

/**
 * Public (no-JWT) twin of authUploadFile for the GUEST scan endpoint: same
 * native uploadAsync robustness, no token dance. Server errors (429 limit)
 * come back as normal envelopes with the login-wall message.
 */
async function publicUploadFile<T>(
  path: string,
  file: { uri: string; name: string; mimeType: string },
  fields: Record<string, string> = {},
): Promise<ApiEnvelope<T>> {
  if (Platform.OS === 'web') {
    try {
      const form = new FormData();
      const blob = await (await fetch(file.uri)).blob();
      form.append('file', new File([blob], file.name, { type: file.mimeType }));
      for (const [k, v] of Object.entries(fields)) form.append(k, v);
      const response = await fetch(`${API_URL}${path}`, { method: 'POST', body: form });
      return (await response.json()) as ApiEnvelope<T>;
    } catch {
      return { status: false, message: 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.', data: null };
    }
  }
  try {
    const { uploadAsync, FileSystemUploadType } = await import('expo-file-system/legacy');
    const result = await uploadAsync(`${API_URL}${path}`, file.uri, {
      httpMethod: 'POST',
      uploadType: FileSystemUploadType.MULTIPART,
      fieldName: 'file',
      mimeType: file.mimeType,
      parameters: fields,
    });
    return JSON.parse(result.body) as ApiEnvelope<T>;
  } catch {
    return { status: false, message: 'Brak połączenia z serwerem. Sprawdź internet i spróbuj ponownie.', data: null };
  }
}

async function rawUpload(path: string, form: FormData, token: string | null) {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_URL}${path}`, { method: 'POST', headers, body: form });
}

async function rawFetch(path: string, method: string, body: unknown, token: string | null) {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

// Single-flight refresh. The refresh token ROTATES on every use and the backend
// treats a reused (already-rotated) token as theft — revoking the whole session.
// On startup several callers (refreshMe, syncNow, policy loads) can race into
// refresh at once; without this guard the 2nd one reuses the just-rotated token
// and nukes the session. Sharing one in-flight promise makes them all wait for
// the single rotation and reuse its new access token.
let refreshInFlight: Promise<string | null> | null = null;

function tryRefresh(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<string | null> {
  const stored = await getRefreshToken();
  if (!stored) return null;
  const result = await refreshSession(stored);
  if (!result.ok || !result.data) return null;
  await useAuthStore.getState().applySession(result.data);
  return result.data.accessToken;
}

// ----------------------------------------------------------------- mock layer

function mockLink(): AuthResult<string> {
  return { ok: true, message: 'Konto połączone.', data: 'LINKED' };
}

function mockLinkByPesel(pesel: string): AuthResult<string> {
  if (pesel.replace(/\D/g, '') === '80010112345') {
    return { ok: true, message: 'Konto połączone.', data: 'LINKED' };
  }
  return {
    ok: false,
    message: 'Ten PESEL nie pasuje do klienta z Twoim numerem telefonu. Poproś o pomoc — sprawdzimy ręcznie.',
  };
}

function mockReferralInfo(): AuthResult<ReferralInfo> {
  return {
    ok: true,
    message: null,
    data: { code: 'POL-392-FAM', shareUrl: 'https://polfinance.pl/aplikacja?ref=POL-392-FAM', rewardPln: 50 },
  };
}

function mockScanDraft(): AuthResult<PolicyScanDraft> {
  return {
    ok: true,
    message: 'Odczytano zdjęcie polisy',
    data: {
      policyType: 'CAR_AC',
      insurerName: 'PZU',
      policyNumber: 'PZ-2026-777',
      subject: 'Toyota RAV4, WW 12345',
      premiumGr: 189_050,
      premiumPeriod: 'ANNUAL',
      startDate: '2026-03-15',
      endDate: '2027-03-14',
      warning: null,
    },
  };
}

function mockAgent(): AuthResult<AgentCard> {
  return { ok: true, message: null, data: { name: 'Marek Nowak', phone: '+48 600 100 200' } };
}

// Guest scan mock: enforces the same 4/day + 8 lifetime wall locally so the
// login-wall UX is E2E-testable on the web preview.
let mockGuestScansToday = 0;
let mockGuestScansTotal = 0;

function mockGuestScanDraft(): AuthResult<PolicyScanDraft> {
  if (mockGuestScansTotal >= 8) {
    return { ok: false, message: 'Wykorzystane darmowe odczyty AI. Zaloguj się, aby skanować dalej (do 10 dziennie).' };
  }
  if (mockGuestScansToday >= 4) {
    return { ok: false, message: 'Limit 4 odczytów AI na dziś wykorzystany. Wróć jutro albo zaloguj się.' };
  }
  mockGuestScansToday += 1;
  mockGuestScansTotal += 1;
  return mockScanDraft();
}

function mockRequestAccountPhone(): AuthResult<undefined> {
  console.warn('[mock] account phone OTP: 123456');
  return { ok: true, message: 'Kod wysłany SMS-em.' };
}

function mockVerifyAccountPhone(code: string, phone?: string): AuthResult<AuthPayload> {
  if (code !== '123456') {
    return { ok: false, message: 'Nieprawidłowy kod.' };
  }
  const account = useAuthStore.getState().account;
  return {
    ok: true,
    message: 'Numer potwierdzony.',
    data: {
      accessToken: `mock-access-${Date.now()}`,
      refreshToken: `mock-refresh-${Math.random().toString(36).slice(2)}`,
      account: {
        id: account?.id ?? 2,
        phone: account?.phone ?? phone ?? '+48500600700',
        email: account?.email ?? null,
        firstName: account?.firstName ?? null,
        linkStatus: account?.linkStatus ?? 'NONE',
      },
      matchStatus: 'NONE',
      maskedMatch: null,
      requiresSmsVerify: false,
    },
  };
}

const MOCK_AGENT_CLIENTS: AgentClient[] = [
  {
    id: 11,
    name: 'Anna Kowalska',
    phone: '+48 512 300 400',
    city: 'Wieluń',
    policiesCount: 3,
    lastPolicyEndDate: '2027-03-14',
    hasApp: true,
  },
  {
    id: 12,
    name: 'Jan Malinowski',
    phone: '+48 604 700 800',
    city: 'Sieradz',
    policiesCount: 1,
    lastPolicyEndDate: '2026-08-02',
    hasApp: false,
  },
  {
    id: 13,
    name: 'Ewa Wiśniewska',
    phone: '+48 725 111 222',
    city: 'Wieluń',
    policiesCount: 5,
    lastPolicyEndDate: '2027-01-09',
    hasApp: true,
  },
];

function mockAgentClients(): AuthResult<AgentClient[]> {
  return { ok: true, message: null, data: MOCK_AGENT_CLIENTS };
}

// In-memory mock chat: one conversation, agent "answers" after the 2nd message.
const mockChatMessages: ChatMessage[] = [];
let mockChatNextId = 1;

function mockChatStart(): AuthResult<ChatPollResult> {
  return {
    ok: true,
    message: null,
    data: {
      conversationId: 900,
      status: 'OPEN',
      agentJoined: mockChatMessages.some((m) => m.author === 'AGENT'),
      messages: mockChatMessages,
    },
  };
}

function mockChatSend(body: string): AuthResult<ChatMessage> {
  const msg: ChatMessage = {
    id: mockChatNextId++,
    author: 'VISITOR',
    body,
    createdAt: new Date().toISOString(),
  };
  mockChatMessages.push(msg);
  if (mockChatMessages.filter((m) => m.author === 'VISITOR').length >= 2) {
    mockChatMessages.push({
      id: mockChatNextId++,
      author: 'AGENT',
      body: 'Jasne, przygotuję wycenę na dziś — na ile osób?',
      createdAt: new Date().toISOString(),
    });
  }
  return { ok: true, message: null, data: msg };
}

function mockChatPoll(afterId: number): AuthResult<ChatPollResult> {
  return {
    ok: true,
    message: null,
    data: {
      conversationId: 900,
      status: mockChatMessages.length ? 'OPEN' : 'NONE',
      agentJoined: mockChatMessages.some((m) => m.author === 'AGENT'),
      messages: mockChatMessages.filter((m) => m.id > afterId),
    },
  };
}

function mockAgentConversations(): AuthResult<AgentChatConversation[]> {
  return {
    ok: true,
    message: null,
    data: [
      {
        id: 900,
        status: 'OPEN',
        channel: 'APP',
        visitorName: 'Anna Kowalska',
        lastMessagePreview: 'Jadę na urlop, potrzebuję ubezpieczenia',
        lastVisitorMsgAt: new Date().toISOString(),
        unread: true,
      },
    ],
  };
}

function mockAgentMessages(conversationId: number, afterId: number): AuthResult<AgentChatMessagesResult> {
  const conv = mockAgentConversations().data![0];
  const msgs: ChatMessage[] = [
    {
      id: 1,
      author: 'VISITOR',
      body: 'Jadę na urlop do Chorwacji, potrzebuję ubezpieczenia turystycznego na 2 tygodnie',
      createdAt: new Date().toISOString(),
    },
  ];
  return {
    ok: true,
    message: null,
    data: { conversation: conv, messages: msgs.filter((m) => m.id > afterId) },
  };
}

function mockAgentClientDetail(customerId: number): AuthResult<AgentClientDetail> {
  const client = MOCK_AGENT_CLIENTS.find((c) => c.id === customerId) ?? MOCK_AGENT_CLIENTS[0];
  const policies = mockPolicies().data ?? [];
  return {
    ok: true,
    message: null,
    data: {
      id: client.id,
      name: client.name,
      phone: client.phone,
      email: 'anna@example.com',
      city: client.city,
      hasApp: client.hasApp,
      policies,
    },
  };
}

function mockPolicies(): AuthResult<PolfinancePolicy[]> {
  return {
    ok: true,
    message: null,
    data: [
      {
        id: 101,
        policyNumber: 'PZ-2026-001',
        insurerName: 'PZU',
        productName: 'OC + AC pakiet',
        premiumGr: 189_000,
        premiumPeriod: 'ANNUAL',
        startDate: '2026-03-15',
        endDate: '2027-03-14',
        active: true,
        nextTechnicalExamDate: null,
        documentUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Sample_scan.png/480px-Sample_scan.png',
        documentName: 'polisa-pzu.png',
        documentContentType: 'image/png',
      },
      {
        id: 102,
        policyNumber: 'WA-88421',
        insurerName: 'Warta',
        productName: 'Dom pakiet',
        premiumGr: 62_000,
        premiumPeriod: 'ANNUAL',
        startDate: '2026-01-10',
        endDate: '2027-01-09',
        active: true,
        nextTechnicalExamDate: null,
      },
    ],
  };
}
